<template>
  <div>
    <q-input
      type="text"
      readonly
      outlined
      dense
      color="info"
      class="field"
      :value="displayValue"
      :name="name"
      :label="label"
      :disable="isDisabled || isDisplayOnly"
      :data-id="name"
      :dark="isDarkTheme"
      ><q-tooltip v-if="!isDisplayOnly && !isDisabled">
        This value (json dict) will be used as the value for
        <strong>{{ label }}</strong> in this test.
      </q-tooltip>
    </q-input>

    <q-popup-edit
      persistent
      v-model="jsonValue"
      :validate="validateJson"
      :disable="isDisplayOnly"
      @save="$emit('save', $event)"
      @before-show="initEditor(value)"
      @show="$emit('focus')"
      @hide="$emit('blur')"
    >
      <template #default="scope">
        <div class="json-editor" :class="jsonEditorInstanceId" @keyup.enter.stop></div>

        <div class="toolbar">
          <div class="toolbar">
            <div class="validation-error" v-if="!isValid">Invalid json</div>
          </div>
          <div>
            <q-btn flat dense outline color="secondary" icon="close" label="cancel" @click.stop="scope.cancel" />
            <q-btn
              flat
              dense
              color="secondary"
              icon="check_circle"
              @click.stop="scope.set"
              label="apply"
              :disable="validateJson(scope.value) === false"
            />
          </div>
        </div>
      </template>
    </q-popup-edit>
  </div>
</template>
<script lang="ts">
import { generateId } from '@/utils/generateId'
import { useDirectStore } from '@/utils/utils'
import { editor } from 'monaco-editor'
import { computed, defineComponent, ref } from 'vue'

export default defineComponent({
  props: {
    isDisplayOnly: {
      type: Boolean,
      required: false,
    },
    label: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: true,
    },
    name: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const jsonValue = ref('')
    const isValid = ref(true)
    const { getters } = useDirectStore()

    const jsonEditorInstanceId = `json-editor-${generateId()}`
    const isDarkTheme = computed(() => getters.themes.isDarkTheme)

    function validateJson(val: string) {
      try {
        return !!JSON.parse(val)
      } catch {
        return false
      }
    }

    function initEditor(value: string) {
      setTimeout(() => {
        jsonValue.value = value
        const el = document.querySelector(`.${jsonEditorInstanceId}`)

        const jsonEditor = editor.create(el as HTMLElement, {
          value: jsonValue.value,
          language: 'json',
          automaticLayout: false,
          wordWrap: 'on',
          minimap: {
            enabled: false,
          },
        })

        jsonEditor.onDidChangeModelContent(() => {
          const content = jsonEditor.getValue()
          isValid.value = validateJson(content)
          jsonValue.value = content
        })
      }, 5)
    }

    // Remove spaces from JSON stringified value to improve
    // readability on readonly input
    const displayValue = computed(() => props.value.replaceAll(/\s+/g, ''))

    return {
      displayValue,
      initEditor,
      isDarkTheme,
      isValid,
      jsonEditorInstanceId,
      jsonValue,
      validateJson,
    }
  },
})
</script>
<style scoped lang="scss">
@import '@/styles/quasar.variables.scss';

.json-editor {
  min-height: 300px;
  height: 50vh;
  width: 500px;
}

.field {
  flex: 1;
  overflow: hidden;
  max-width: 38vw;

  ::v-deep input {
    font-family: Menlo, Monaco, 'Courier New', monospace;
    font-size: small;
    cursor: pointer !important;
  }

  ::v-deep .q-field__control:before {
    // Remove Quasar dashed border for readonly inputs
    border-style: solid !important;
  }
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-content: center;
}

.validation-error {
  color: $negative;
}
</style>
